import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RecurrenceRule, ScheduleTarget, TaskLink, TemplateLine } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { useToast } from '../../context/ToastContext';
import { ForeignList, taskApi, taskErrorMessage } from '../../utils/taskApi';
import { expandOccurrences, isRuleComplete } from '../../utils/recurrence';
import { PLACEHOLDER_HELP, fillPlaceholders, hasPlaceholders } from '../../utils/placeholders';
import { addDays, isoWeekday, parseDay, shortDate } from '../../utils/taskDates';
import { LINK_NOUN } from '../../utils/taskLinks';
import { firstName, userName } from '../../utils/taskUsers';
import RecurrencePicker from './RecurrencePicker';
import TaskDialog from './TaskDialog';
import TaskIcon from './TaskIcon';
import { UserBubble } from './UserBubble';
import '../../tasks.css';

type When = 'now' | 'date' | 'repeat';

/** "- sub-item" / "-- sub-sub-item" lines → template lines. */
function parseLines(text: string): TemplateLine[] {
  return text
    .split('\n')
    .map((raw) => {
      const m = /^(\s*)(-*)\s*(.*)$/.exec(raw) || ['', '', '', raw];
      const indent = Math.floor(m[1].replace(/\t/g, '  ').length / 2);
      return { text: m[3].trim(), depth: Math.min(2, Math.max(indent, m[2].length)) };
    })
    .filter((l) => l.text);
}

function linesToText(lines: TemplateLine[]): string {
  return lines.map((l) => `${'-'.repeat(l.depth)}${l.depth ? ' ' : ''}${l.text}`).join('\n');
}

// Quick add, scheduling and recurring tasks in one place. Opened from the
// sidebar "New task" button, any record's "Add task" button (pre-linked), the
// Lists page, and the Calendar (pre-dated).
const TaskComposer: React.FC = () => {
  const { composer, closeComposer, users, me, today, lists: allLists, weeklyListId, canEditList, createItem, refresh } = useTasks();
  // Only lists I can add to: not a week list I see just for my own lines, nor
  // a list I can only read because one of its tasks is on the week list.
  // A project's Lists card has no items of its own — add to the project instead.
  const lists = useMemo(() => allLists.filter((l) => l.kind !== 'view' && canEditList(l)), [allLists, canEditList]);
  const { showToast } = useToast();
  const preset = composer || {};
  const editing = preset.schedule || null;

  const [text, setText] = useState(editing?.title || preset.text || '');
  const [link, setLink] = useState<TaskLink | undefined>(preset.link);
  const [when, setWhen] = useState<When>(
    editing ? (editing.recurrence ? 'repeat' : 'date') : preset.when || (preset.date && preset.date > today ? 'date' : 'now')
  );
  // This week's list unless the person isn't on it (Admin › Notifications),
  // in which case their first list.
  const [listId, setListId] = useState<number | null>(
    preset.listId ?? (lists.some((l) => l.id === weeklyListId) ? weeklyListId : lists[0]?.id ?? null)
  );
  const [date, setDate] = useState(
    editing?.start_date || (preset.date && preset.date > today ? preset.date : preset.when === 'repeat' ? today : addDays(today, 1))
  );
  const [dateTouched, setDateTouched] = useState(!!editing || !!preset.date);
  const [dueDate, setDueDate] = useState(preset.date && preset.date <= today ? preset.date : '');
  const [target, setTarget] = useState<ScheduleTarget>(editing?.target || 'weekly');
  const [targetListId, setTargetListId] = useState<number | null>(editing?.target_list_id ?? null);
  const [rule, setRule] = useState<RecurrenceRule>(
    editing?.recurrence || { type: 'weekdays', weekdays: [isoWeekday(preset.date || today)], every: 7, monthDays: [parseDay(preset.date || today).getDate()] }
  );
  const [until, setUntil] = useState(editing?.recurrence?.until || '');
  const [assignees, setAssignees] = useState<string[]>(editing ? editing.assignees || [] : [me]);
  const toggleAssignee = (email: string) =>
    setAssignees((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));
  const [note, setNote] = useState(editing?.note || '');
  const [showNote, setShowNote] = useState(!!editing?.note);
  const [linesText, setLinesText] = useState(editing ? linesToText(editing.items || []) : '');
  const [showLines, setShowLines] = useState(!!editing?.items?.length);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admins can hand work straight onto the lists the assignees work from,
  // including private ones they can't otherwise see.
  const isAdmin = users.find((u) => u.email === me)?.user_type === 'Admin';
  const [foreignLists, setForeignLists] = useState<ForeignList[]>([]);
  const othersKey = assignees.filter((a) => a !== me).sort().join(',');
  useEffect(() => {
    if (!isAdmin || !othersKey) {
      setForeignLists([]);
      return;
    }
    let cancelled = false;
    taskApi
      .listsFor(othersKey.split(','))
      .then((r) => !cancelled && setForeignLists(r.lists))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isAdmin, othersKey]);

  /** Every list this task could go on: this week's first, then mine, then other people's. */
  const listOptions = useMemo(() => {
    const own = lists.map((l) => ({ id: l.id, title: l.title, owner: l.owner_email, weekly: l.kind === 'weekly' }));
    const seen = new Set(own.map((l) => l.id));
    const others = foreignLists
      .filter((l) => !seen.has(l.id))
      .map((l) => ({ id: l.id, title: l.title, owner: l.owner_email, weekly: false }));
    const byTitle = (a: { title: string }, b: { title: string }) => (a.title || '').localeCompare(b.title || '');
    const weekly = own.filter((l) => l.id === weeklyListId);
    const mine = own.filter((l) => l.id !== weeklyListId).sort(byTitle);
    const all = [...weekly, ...mine, ...others.sort(byTitle)];
    // Editing a schedule whose list we can't list any more: keep it selectable.
    if (targetListId && !all.some((l) => l.id === targetListId)) {
      all.push({ id: targetListId, title: 'Current list', owner: '', weekly: false });
    }
    return all;
  }, [lists, foreignLists, weeklyListId, targetListId]);

  const optionLabel = (l: { id: number; title: string; owner: string }) => {
    const name = l.title || 'Untitled list';
    if (l.id === weeklyListId) return `${name} (this week)`;
    if (lists.find((x) => x.id === l.id)?.kind === 'project') return `${name} (project)`;
    return l.owner && l.owner !== me && l.owner !== 'system' ? `${name} — ${firstName(users, l.owner)}'s` : name;
  };

  // "Add to an existing list": start on the first list belonging to whoever
  // it's for, which is usually the point.
  useEffect(() => {
    if (target !== 'list' || targetListId) return;
    const forAssignee = listOptions.find((l) => !l.weekly && assignees.includes(l.owner));
    const first = forAssignee || listOptions.find((l) => !l.weekly);
    if (first) setTargetListId(first.id);
  }, [target, targetListId, listOptions, assignees]);

  const lines = parseLines(linesText);
  const canSubmit =
    !!text.trim() &&
    !saving &&
    (when !== 'repeat' || isRuleComplete(rule)) &&
    (when !== 'now' || !!listId) &&
    (when === 'now' || !!date) &&
    (when === 'now' || target !== 'list' || !!targetListId);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      if (when === 'now') {
        // A task added now is for today, so placeholders fill in with today.
        const { done } = createItem({
          list_id: listId!,
          text: fillPlaceholders(text.trim(), today),
          assignees,
          note: note.trim() ? fillPlaceholders(note.trim(), today) : null,
          due_date: dueDate || null,
          ...(link || {}),
        });
        const root = await done;
        if (!root) throw new Error('The task could not be added');
        // Sub-items sit under the task (one level), or under the sub-item
        // above them (two levels) — the deepest a list goes.
        let lastChild: number | null = null;
        for (const line of lines) {
          const nested: boolean = line.depth > 0 && lastChild !== null;
          const created: { id: number } | null = await createItem({
            list_id: listId!,
            parent_id: nested ? lastChild : root.id,
            text: fillPlaceholders(line.text, today),
          }).done;
          if (created && !nested) lastChild = created.id;
        }
        const picked = listOptions.find((l) => l.id === listId);
        showToast(
          listId === weeklyListId
            ? "Added to this week's list"
            : picked && picked.owner !== me && picked.owner
              ? `Added to ${firstName(users, picked.owner)}'s "${picked.title || 'list'}"`
              : 'Task added',
          'success'
        );
      } else {
        const body = {
          title: text.trim(),
          target,
          target_list_id: target === 'list' ? targetListId : null,
          start_date: date,
          recurrence: when === 'repeat' ? { ...rule, until: until || null } : null,
          items: lines,
          note: note.trim() || null,
          assignees,
        };
        if (editing) {
          await taskApi.updateSchedule(editing.id, body);
          showToast('Schedule updated', 'success');
        } else {
          const result = await taskApi.createSchedule(body);
          showToast(
            result.added
              ? target === 'weekly' ? "Added to this week's list" : target === 'list' ? 'Added to the list' : 'New list created'
              : when === 'repeat' ? 'Recurring task scheduled' : `Scheduled for ${shortDate(date)}`,
            'success'
          );
          if (result.added) refresh();
        }
        window.dispatchEvent(new Event('tasks:schedules-changed'));
      }
      closeComposer();
    } catch (err) {
      setError(taskErrorMessage(err, 'Could not save the task'));
      setSaving(false);
    }
  };

  const removeSchedule = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await taskApi.deleteSchedule(editing.id);
      window.dispatchEvent(new Event('tasks:schedules-changed'));
      showToast('Schedule deleted', 'success');
      closeComposer();
    } catch (err) {
      setError(taskErrorMessage(err, 'Could not delete the schedule'));
      setSaving(false);
    }
  };

  const togglePaused = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await taskApi.updateSchedule(editing.id, { active: !editing.active });
      window.dispatchEvent(new Event('tasks:schedules-changed'));
      showToast(editing.active ? 'Paused' : 'Resumed', 'success');
      closeComposer();
    } catch (err) {
      setError(taskErrorMessage(err, 'Could not update the schedule'));
      setSaving(false);
    }
  };

  // An untouched start date follows the mode: a repeat starts today, a
  // one-off defaults to tomorrow.
  const changeWhen = (next: When) => {
    setWhen(next);
    if (!dateTouched) setDate(next === 'repeat' ? today : addDays(today, 1));
  };
  const pickDate = (value: string) => {
    setDate(value);
    setDateTouched(true);
  };

  // Placeholders ([month] blog post): insert at the cursor, and show what the
  // next one will actually be called.
  const textRef = useRef<HTMLTextAreaElement>(null);
  const insertToken = (token: string) => {
    const el = textRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const before = text.slice(0, start);
    const spacer = before && !/\s$/.test(before) ? ' ' : '';
    const next = `${before}${spacer}${token}${text.slice(end)}`;
    setText(next);
    requestAnimationFrame(() => {
      const at = start + spacer.length + token.length;
      el?.focus();
      el?.setSelectionRange(at, at);
    });
  };
  const preview = useMemo(() => {
    if (!hasPlaceholders(text)) return null;
    let day: string | null = today;
    let label = 'Today';
    if (when === 'date') {
      day = date;
      label = `On ${shortDate(date)}`;
    } else if (when === 'repeat') {
      const from = date > today ? date : today;
      day = expandOccurrences(rule, from, addDays(from, 400), date)[0] || null;
      label = day ? `Next one, ${shortDate(day)}` : 'Next one';
    }
    return day ? { label, text: fillPlaceholders(text.trim(), day) } : null;
  }, [text, when, date, rule, today]);

  const submitLabel = editing ? 'Save' : when === 'now' ? 'Add task' : 'Schedule';

  return (
    <TaskDialog
      title={editing ? (editing.recurrence ? 'Recurring task' : 'Scheduled task') : 'New task'}
      onClose={closeComposer}
      footer={
        <>
          {editing && (
            <div className="tk-foot-left">
              <button type="button" className="tk-btn tk-btn-ghost tk-btn-danger-text" onClick={removeSchedule} disabled={saving}>Delete</button>
              {editing.recurrence && (
                <button type="button" className="tk-btn tk-btn-ghost" onClick={togglePaused} disabled={saving}>
                  {editing.active ? 'Pause' : 'Resume'}
                </button>
              )}
            </div>
          )}
          <button type="button" className="tk-btn tk-btn-ghost" onClick={closeComposer}>Cancel</button>
          <button type="button" className="tk-btn tk-btn-primary" onClick={() => submit()} disabled={!canSubmit}>
            {saving ? 'Saving…' : submitLabel}
          </button>
        </>
      }
    >
      <form className="tk-composer" onSubmit={submit}>
        <textarea
          ref={textRef}
          className="tk-input tk-composer-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
          }}
          placeholder={target === 'new_list' && when !== 'now' ? 'List title' : 'What needs doing?'}
          rows={2}
          autoFocus
          aria-label="Task"
        />

        {(when !== 'now' || hasPlaceholders(text)) && (
          <div className="tk-placeholders">
            <span className="tk-placeholders-label">Insert</span>
            {PLACEHOLDER_HELP.map((p) => (
              <button
                key={p.token}
                type="button"
                className="tk-chip-btn"
                onClick={() => insertToken(p.token)}
                title={`Becomes e.g. "${p.example}" — filled in for each date it lands on`}
              >
                {p.token}
              </button>
            ))}
          </div>
        )}
        {preview && (
          <p className="tk-placeholder-preview">
            <TaskIcon name="scheduled" size={13} />
            {preview.label}: <strong>{preview.text}</strong>
          </p>
        )}

        {link && (
          <div className="tk-composer-link">
            <span className="tk-chip tk-chip-link">
              <TaskIcon name="link" size={11} /> {LINK_NOUN[link.link_type]}: {link.link_label || link.link_ref}
            </span>
            <button type="button" className="tk-icon-btn tk-icon-btn-sm" onClick={() => setLink(undefined)} aria-label="Remove link">
              <TaskIcon name="close" size={12} />
            </button>
          </div>
        )}

        <div className="tk-field">
          <span className="tk-field-label">When</span>
          <div className="tk-segmented" role="group" aria-label="When">
            {!editing && <button type="button" className={when === 'now' ? 'is-active' : ''} onClick={() => changeWhen('now')}>Now</button>}
            <button type="button" className={when === 'date' ? 'is-active' : ''} onClick={() => changeWhen('date')}>On a date</button>
            <button type="button" className={when === 'repeat' ? 'is-active' : ''} onClick={() => changeWhen('repeat')}>Repeats</button>
          </div>
        </div>

        {when === 'now' && (
          <div className="tk-field-row">
            <label className="tk-field">
              <span className="tk-field-label">Add to</span>
              <select className="tk-input" value={listId ?? ''} onChange={(e) => setListId(Number(e.target.value))}>
                {listOptions.map((l) => (
                  <option key={l.id} value={l.id}>{optionLabel(l)}</option>
                ))}
              </select>
            </label>
            <label className="tk-field tk-field-narrow">
              <span className="tk-field-label">Due <span className="tk-muted">(optional)</span></span>
              <input type="date" className="tk-input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </label>
          </div>
        )}

        {when === 'date' && (
          <label className="tk-field">
            <span className="tk-field-label">Show up on</span>
            <input type="date" className="tk-input" value={date} min={today} onChange={(e) => pickDate(e.target.value)} />
          </label>
        )}

        {when === 'repeat' && (
          <>
            <RecurrencePicker value={rule} onChange={setRule} />
            <div className="tk-field-row">
              <label className="tk-field">
                <span className="tk-field-label">Starting</span>
                <input type="date" className="tk-input" value={date} onChange={(e) => pickDate(e.target.value)} />
              </label>
              <label className="tk-field">
                <span className="tk-field-label">Ends <span className="tk-muted">(optional)</span></span>
                <input type="date" className="tk-input" value={until} min={date} onChange={(e) => setUntil(e.target.value)} />
              </label>
            </div>
          </>
        )}

        {when !== 'now' && (
          <div className="tk-field">
            <span className="tk-field-label">On that day</span>
            <div className="tk-choice-row">
              <label className={`tk-choice${target === 'weekly' ? ' is-active' : ''}`}>
                <input type="radio" name="target" checked={target === 'weekly'} onChange={() => setTarget('weekly')} />
                <TaskIcon name="pin" size={15} />
                <span>
                  <strong>Add to MPP's weekly list</strong>
                  <small>One line on that week's list</small>
                </span>
              </label>
              <label className={`tk-choice${target === 'new_list' ? ' is-active' : ''}`}>
                <input type="radio" name="target" checked={target === 'new_list'} onChange={() => setTarget('new_list')} />
                <TaskIcon name="list" size={15} />
                <span>
                  <strong>Create as a new list</strong>
                  <small>Its own card, shared with whoever it's for</small>
                </span>
              </label>
              <label className={`tk-choice${target === 'list' ? ' is-active' : ''}`}>
                <input type="radio" name="target" checked={target === 'list'} onChange={() => setTarget('list')} />
                <TaskIcon name="assigned" size={15} />
                <span>
                  <strong>Add to an existing list</strong>
                  <small>e.g. someone's own running list</small>
                </span>
              </label>
            </div>
            {target === 'list' && (
              <label className="tk-field tk-target-list">
                <span className="tk-field-label">Which list</span>
                <select className="tk-input" value={targetListId ?? ''} onChange={(e) => setTargetListId(Number(e.target.value) || null)}>
                  {!targetListId && <option value="">Choose a list…</option>}
                  {listOptions.filter((l) => !l.weekly).map((l) => (
                    <option key={l.id} value={l.id}>{optionLabel(l)}</option>
                  ))}
                </select>
                {isAdmin && !assignees.some((a) => a !== me) && (
                  <small className="tk-muted">Pick who it's for below to see their lists too.</small>
                )}
              </label>
            )}
          </div>
        )}

        <div className="tk-field">
          <span className="tk-field-label">
            {when !== 'now' && target === 'new_list' ? 'For' : 'Assign to'}
            <span className="tk-muted">
              {' · '}
              {assignees.length ? assignees.map((a) => userName(users, a)).join(', ') : 'nobody'}
            </span>
          </span>
          <div className="tk-assign-row" role="group" aria-label="Assign to — pick one or more">
            <button
              type="button"
              className={`tk-assign-opt${assignees.length === 0 ? ' is-active' : ''}`}
              onClick={() => setAssignees([])}
              title="Nobody"
            >
              <span className="tk-bubble tk-bubble-md tk-bubble-empty"><TaskIcon name="user" size={13} /></span>
            </button>
            {users.map((u) => (
              <button
                key={u.email}
                type="button"
                className={`tk-assign-opt${assignees.includes(u.email) ? ' is-active' : ''}`}
                onClick={() => toggleAssignee(u.email)}
                title={userName(users, u.email)}
                aria-pressed={assignees.includes(u.email)}
              >
                <UserBubble users={users} email={u.email} size="md" title="" />
              </button>
            ))}
          </div>
        </div>

        {showLines ? (
          <label className="tk-field">
            <span className="tk-field-label">
              {when !== 'now' && target === 'new_list' ? 'Items' : 'Sub-items'} <span className="tk-muted">— one per line, start with "-" to nest</span>
            </span>
            <textarea className="tk-input tk-textarea" rows={4} value={linesText} onChange={(e) => setLinesText(e.target.value)} placeholder={'Count stock\n- Small boxes\n- Large boxes'} />
          </label>
        ) : (
          <button type="button" className="tk-link-btn tk-add-extra" onClick={() => setShowLines(true)}>
            <TaskIcon name="list" size={14} /> Add {when !== 'now' && target === 'new_list' ? 'items' : 'sub-items'}
          </button>
        )}

        {showNote ? (
          <label className="tk-field">
            <span className="tk-field-label">Note</span>
            <textarea className="tk-input tk-textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Shows when you hover over the item" />
          </label>
        ) : (
          <button type="button" className="tk-link-btn tk-add-extra" onClick={() => setShowNote(true)}>
            <TaskIcon name="note" size={14} /> Add note
          </button>
        )}

        {error && <p className="tl-error">{error}</p>}
      </form>
    </TaskDialog>
  );
};

/** Mounted once in App; renders whenever something calls openComposer(). */
export const TaskComposerHost: React.FC = () => {
  const { composer } = useTasks();
  // Re-key so each open starts from its own preset.
  const [openCount, setOpenCount] = useState(0);
  const [last, setLast] = useState(composer);
  if (composer !== last) {
    setLast(composer);
    if (composer) setOpenCount((n) => n + 1);
  }
  return composer ? <TaskComposer key={openCount} /> : null;
};

export default TaskComposer;
